/* eslint-disable max-len */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useTheme } from 'emotion-theming';
import Text from '../../../shared/components/Text/Text';
import Spinner from '../../../shared/_TBComponents/Spinner';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import useRefetchRecurringData from '../components/presentational/hooks/useRefetchRecurringData';
import {
  deleteSavedCalendarItem,
  deleteSavedWorkoutItem,
  setDeleteStack,
  deleteSavedWorkoutItemsForWeek,
  removePresaveProgramItemFromRedux,
  removePresaveItemFromRedux,
  duplicateWeek,
  fetchNonDateBasedProgramItemsByDayRange,
  deleteRecurringItem,
} from '../ducks/calendarActions';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
`;

const SpinnerWrapper = styled('div')`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  width: 100%;
`;

const IconCircle = styled('div')`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalText = styled('div')`
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
`;

const WarningText = styled('div')`
  margin-top: 16px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.warningSurface};
  border: 1px solid ${({ theme }) => theme.colors.warningBorder};
  color: ${({ theme }) => theme.colors.warningText};
  p { color: inherit; }
  border-radius: 4px;
  display: flex;
  align-items: flex-start;
  text-align: left;
  width: 100%;
`;

const SubHeaderText = styled('div')`
  margin-bottom: 24px;
  text-align: left;
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`;

const ButtonsContainer = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DeleteButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 48px;
  background: #3C3C3C;
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #2C2C2C;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: #3C3C3C;
  }
`;

const DuplicateButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 48px;
  background: #3C3C3C;
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 8px;

  &:hover {
    background: #2C2C2C;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: #3C3C3C;
  }
`;

const CancelButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 48px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid #D0D0D0;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const CloseButton = styled('div')`
  position: absolute;
  top: 0;
  right: 0;
  padding: 10px;
  cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.isDisabled ? 'none' : 'auto')};
`;

const LinkText = styled('a')`
  color: #007AFF;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ConfirmDeleteModal = ({ closeModal, setIsCloseLocked }) => {
  const dispatch = useDispatch();
  const refetchRecurringData = useRefetchRecurringData();
  const modalData = useSelector((state) => state.calendar.data.modalData);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountCode = currentUser?.accountCode;
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const calendarPickerFirstMonth = useSelector((state) => state.calendar.data.firstMonthInPicker);
  const deleteStack = useSelector((state) => state.calendar.data.deleteStack);
  const programType = useSelector((state) => state.calendar.data.programType);
  const { colors } = useTheme();
  const calendarItems = programType === 'free-form-program'
    ? useSelector((state) => state.calendar.data.nonDateBasedProgramItems) : useSelector((state) => state.calendar.data.calendarItems);
  const dateToUse = modalData?.date || selectedDate;
  const formattedDate = moment(dateToUse).format('YYYY-MM-DD');
  const itemCount = ((modalData?.bulkDeleteAssignedIds?.length || 0)
  + (modalData?.preSaveItems?.length || 0)) || 1;
  const itemText = itemCount === 1 ? 'workout item' : 'workout items';
  const isRecurring = modalData?.isRecurring;

  // eslint-disable-next-line no-unused-vars
  const [deleteOption, setDeleteOption] = useState('single');
  const [isLoading, setIsLoading] = useState(false);

  /** flips local spinner state and the parent close-lock together so backdrop /
   * Escape / X-button can't dismiss the modal mid-request
   */
  const startAsync = () => {
    setIsLoading(true);
    setIsCloseLocked(true);
  };
  const endAsync = () => {
    setIsLoading(false);
    setIsCloseLocked(false);
  };

  const currentMonth = moment(selectedDate).startOf('month').toDate();
  const nextMonth = moment(selectedDate).add(1, 'month').startOf('month').toDate();

  const handleRemovePresaveItemFromRedux = (assigned, dayNumber) => {
    if (programType === 'free-form-program') {
      dispatch(removePresaveProgramItemFromRedux(dayNumber ?? modalData?.dayNumber, null, assigned));
    } else {
      dispatch(removePresaveItemFromRedux(modalData?.date, null, assigned));
    }
  };

  const handleDelete = () => {
    if (modalData) {
      if (modalData?.preSaveItems?.length > 0) {
        /** Handle preSave items immediately via Redux removal. Week-level clears
         * stamp each pre-save item with its source dayNumber; fall back to
         * modalData.dayNumber for the day-level path.
         */
        modalData?.preSaveItems.forEach((item) => (
          handleRemovePresaveItemFromRedux(item.assignedId, item.dayNumber)
        ));
      }
      /** Get the actual content to add to the delete stack */
      const content = calendarItems[formattedDate]?.exercises[modalData?.itemIndex];
      if (content) {
        /** Add to delete stack */
        const newDeleteStack = [...deleteStack];
        newDeleteStack.push(content);
        dispatch(setDeleteStack(newDeleteStack));
      }

      /** we need to pass the id as an array for the endpoint because it also accomodates a bulk
       * delete it needed
       */
      const assignedIds = modalData?.isBulkDelete ? modalData?.bulkDeleteAssignedIds : [modalData?.assignedId];

      /** Extract exercise names for toast messages */
      let exerciseNames = [];
      if (programType === 'free-form-program') {
        const exercises = calendarItems[modalData?.dayNumber]?.exercises || [];
        exerciseNames = assignedIds
          .map((id) => exercises.find((ex) => ex.assignedId === id))
          .filter(Boolean)
          .map((ex) => ex.title || ex.exercise?.description || ex.description || 'Exercise');
      } else {
        const exercises = calendarItems[formattedDate]?.exercises || [];
        exerciseNames = assignedIds
          .map((id) => exercises.find((ex) => ex.assignedId === id))
          .filter(Boolean)
          .map((ex) => ex.title || ex.exercise?.description || ex.description || 'Exercise');
      }

      /** Delete the item */
      if (programType === 'free-form-program' && assignedIds?.length > 0) {
        startAsync();
        if (modalData?.deleteWeek) {
          dispatch(deleteSavedWorkoutItemsForWeek(
            currentUser.accountCode,
            assignedIds,
            modalData?.weekNumber,
            {
              onSuccess: () => closeModal(),
              onError: () => endAsync(),
            },
          ));
        } else {
          dispatch(deleteSavedWorkoutItem(
            currentUser.accountCode,
            assignedIds,
            modalData?.itemIndex,
            modalData?.dayNumber,
            exerciseNames,
            {
              onSuccess: () => closeModal(),
              onError: () => endAsync(),
            },
          ));
        }
        return;
      }
      if (programType === 'calendar' && assignedIds?.length > 0) {
        if (isRecurring) {
          const [assignedId] = assignedIds ?? [];
          if (!assignedId) return;
          /** when we delete a recurring item schedule, onsuccess we need to
           * fetchCalendarPickerItems so that calendar dots update
           * we also need to re fetch recurring items so that we can get
           * the proper updates in redux. These updates for recurring are too
           * complex to try and update directly in state, so fethcing again is the
           * safest option
           */
          startAsync();
          dispatch(deleteRecurringItem(
            currentUser.accountCode,
            assignedId,
            formattedDate,
            isRecurring,
            {
              onSuccess: () => {
                refetchRecurringData({
                  currentUser,
                  currentMonth,
                  nextMonth,
                  selectedReduxCalendar,
                  selectedReduxAthlete,
                  closeModal,
                  calendarPickerFirstMonth,
                });
              },
              onError: () => endAsync(),
            },
          ));
        } else {
          const calendarItemsBeforeDelete = calendarItems[formattedDate]?.exercises || [];
          startAsync();
          dispatch(deleteSavedCalendarItem(
            currentUser.accountCode,
            assignedIds,
            modalData?.itemIndex,
            formattedDate,
            isRecurring && deleteOption === 'all',
            calendarItemsBeforeDelete,
            exerciseNames,
            {
              onSuccess: () => closeModal(),
              onError: () => endAsync(),
            },
          ));
        }
        return;
      }
    }
    /** preSave-only path (no async work) — close synchronously */
    closeModal();
  };

  const handleDuplicate = () => {
    startAsync();
    dispatch(duplicateWeek(accountCode, modalData?.savedWorkoutId, modalData?.weekNumber, {
      onSuccess: () => {
        const refresh = true;
        dispatch(fetchNonDateBasedProgramItemsByDayRange(
          accountCode,
          modalData?.savedWorkoutId,
          null,
          null,
          refresh,
          modalData?.weekNumber,
        ));
        closeModal();
      },
      onError: () => endAsync(),
    }));
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      if (isLoading) return;
      const tag = e.target.tagName;
      const isCancelButton = e.target === document.querySelector('[data-cancel-button]');
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable || isCancelButton) {
        return;
      }
      e.preventDefault();
      if (modalData?.duplicateWeek) {
        handleDuplicate();
      } else {
        handleDelete();
      }
    }
  }, [modalData, handleDelete, handleDuplicate, isLoading]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <ModalContainer>
      <CloseButton
        isDisabled={isLoading}
        onClick={() => {
          if (isLoading) return;
          closeModal();
        }}
      >
        <IcomoonReact
          iconSet={iconSet}
          size={15}
          icon='remove'
          color={colors.textSecondary}
        />
      </CloseButton>

      {isLoading ? (
        <SpinnerWrapper>
          <Spinner large />
        </SpinnerWrapper>
      ) : (
        <>
          {modalData?.duplicateWeek ? (
            <IconCircle>
              <IcomoonReact
                iconSet={iconSet}
                size={32}
                icon='warning'
              />
            </IconCircle>
          ) : (
            <IconCircle>
              <IcomoonReact
                iconSet={iconSet}
                size={32}
                icon='trashcan'
                color='#DB3030'
              />
            </IconCircle>
          )}

          <ModalText>
            {modalData?.programName && (
              <>
                <Text
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                  }}
                  fontSize='20px'
                  fontWeight={600}
                >
                  {modalData?.programName}
                </Text>
                {modalData?.dayInWeek ? (
                  <Text fontSize='18px'>{`Week ${modalData?.weekNumber} - Day ${modalData?.dayInWeek}`}</Text>
                ) : (
                  <Text fontSize='18px'>{`Week ${modalData?.weekNumber}`}</Text>
                )}
              </>
            )}
            {modalData?.duplicateWeek ? (
              <Text fontSize='20px' fontWeight={600}>
                {`You are about to duplicate Week  ${modalData?.weekNumber}`}
              </Text>
            ) : (
              <Text fontSize='20px' fontWeight={600}>
                {isRecurring ? 'Delete Recurring Instances' : `You are about to delete (${itemCount}) ${itemText}`}
              </Text>
            )}
          </ModalText>

          {isRecurring && (
            <SubHeaderText>
              <Text fontSize='14px' color={colors.textMuted}>
                Are you sure you want to delete? This action will delete all instances of this recurring item.
                {' '}
              </Text>
              <LinkText
                href='https://support.teambuildr.com/article/t5talti5mh-recurring-workout-items'
                target='_blank'
              >
                Click here to learn more.
              </LinkText>
            </SubHeaderText>
          )}
          {modalData?.isParentCalendarItem && (
            <WarningText style={{ marginBottom: 16 }}>
              <IcomoonReact
                iconSet={iconSet}
                size={16}
                icon='warning'
                color='#856404'
                style={{ marginRight: '8px', marginTop: '2px', flexShrink: 0 }}
              />
              {isRecurring ? (
                <Text fontSize='14px' color='#856404'>
                  Warning: you are trying to delete a recurring schedule that belongs to a parent calendar - this action will delete all instances of this recurring item on the parent and child calendar.
                </Text>
              ) : (
                <Text fontSize='14px' color='#856404'>
                  Warning: you are trying to delete an item that belongs to a parent calendar - this action will only affect the item on the child calendar
                </Text>
              )}
            </WarningText>
          )}

          {modalData?.duplicateWeek && (
            <WarningText>
              <IcomoonReact
                iconSet={iconSet}
                size={16}
                icon='warning'
                color='#856404'
                style={{ marginRight: '8px', marginTop: '2px', flexShrink: 0 }}
              />
              <Text fontSize='14px' color='#856404'>
                Warning: duplicating a week will refresh the program and cause any pre save items to be removed. Make sure all items are saved before duplicating.
              </Text>
            </WarningText>
          )}

          {modalData?.deleteWeek && programType === 'free-form-program' && (
            <WarningText style={{ marginBottom: 8 }}>
              <IcomoonReact
                iconSet={iconSet}
                size={16}
                icon='warning'
                color='#856404'
                style={{ marginRight: '8px', marginTop: '2px', flexShrink: 0 }}
              />
              <Text fontSize='14px' color='#856404'>
                Warning: clearing a week will refresh the program. This will cause any unsaved items to be removed from the program and may also cause empty trailing weeks to be removed from the program.
              </Text>
            </WarningText>
          )}
        </>
      )}

      <ButtonsContainer>
        {modalData?.duplicateWeek ? (
          <DuplicateButton onClick={handleDuplicate} disabled={isLoading}>
            DUPLICATE
          </DuplicateButton>
        ) : (
          <DeleteButton onClick={handleDelete} disabled={isLoading}>
            DELETE
          </DeleteButton>
        )}
        <CancelButton data-cancel-button onClick={closeModal} disabled={isLoading}>
          CANCEL
        </CancelButton>
      </ButtonsContainer>
    </ModalContainer>
  );
};

ConfirmDeleteModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  setIsCloseLocked: PropTypes.func.isRequired,
};

export default ConfirmDeleteModal;
